import React, { useEffect, useRef, useState } from "react";
import { useDocumentPages } from "./useDocumentPages";
import type { DocumentPage } from "../../services/documentPagesService";

/**
 * A LinkedIn document post shown the way LinkedIn shows it (SGL-573): its PDF
 * pages as slides, one at a time, in the page's own shape. Arrows, ← →, a swipe
 * or a click on the progress bar move through it; "Open PDF" opens the file.
 *
 * While the pages are being drawn it holds the space with a "Preparing pages…"
 * frame. If they cannot be drawn (deleted, unreadable, a backend without the
 * route), it renders `fallback`: the card's existing PDF tile.
 */

interface Props {
  customerId?: string;
  /** The post's PDFs in publish order (carousel_slides, else document_url). */
  urls: string[];
  idToken?: string;
  /** Shown when there are no pages to show. */
  fallback: React.ReactNode;
}

const fileName = (url: string, index: number) => {
  try {
    return decodeURIComponent(new URL(url).pathname.split("/").pop() || "") || `PDF ${index + 1}`;
  } catch {
    return `PDF ${index + 1}`;
  }
};

const SWIPE_PX = 40;
const NO_PAGES: DocumentPage[] = [];

const ARROW =
  "absolute top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-[8px] border border-theme-border bg-white/90 text-theme-fg shadow-[0_2px_8px_-2px_rgba(0,0,0,0.25)] transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f4941]/40";

const DocumentPagesPreview: React.FC<Props> = ({ customerId, urls, idToken, fallback }) => {
  const state = useDocumentPages(customerId, urls, idToken);
  const [index, setIndex] = useState(0);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const pages = state?.status === "ready" ? state.pages : NO_PAGES;
  const count = pages.length;
  const current = Math.min(index, Math.max(0, count - 1));

  useEffect(() => setIndex(0), [urls.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch the next page before it is asked for, so moving on never shows a blank.
  useEffect(() => {
    const next = pages[current + 1];
    if (next) new Image().src = next.url;
  }, [pages, current]);

  if (!state || state.status === "error") return <>{fallback}</>;

  const go = (to: number) => setIndex(Math.min(count - 1, Math.max(0, to)));

  const header = (
    <div className="flex w-full items-center gap-2 px-0.5 text-xs">
      <span className="material-icons-outlined text-[18px] text-[#2f4941]" aria-hidden="true">
        description
      </span>
      {/* No page count here: the "1 / 12" on the page says it. */}
      <span className="font-semibold text-theme-fg">PDF document</span>
      <span className="ml-auto flex items-center gap-2">
        {urls.map((url, i) => (
          <a
            key={url}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title={fileName(url, i)}
            className="inline-flex items-center gap-1 rounded-[6px] px-1.5 py-1 font-medium text-brand-primary underline-offset-2 transition-colors hover:bg-theme-secondary/10 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f4941]/40"
          >
            <span className="material-icons-outlined text-[15px]" aria-hidden="true">
              open_in_new
            </span>
            {urls.length === 1 ? "Open PDF" : `PDF ${i + 1}`}
          </a>
        ))}
      </span>
    </div>
  );

  if (state.status === "loading") {
    return (
      <div className="flex w-full flex-col gap-2">
        {header}
        <div
          role="status"
          className="flex aspect-[4/5] max-h-[520px] w-full flex-col items-center justify-center gap-3 rounded-lg border border-theme-border bg-theme-background"
        >
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-[#2f4941]/20 border-t-[#2f4941] motion-reduce:animate-none" />
          <span className="text-xs text-theme-fg-muted">Preparing pages…</span>
        </div>
      </div>
    );
  }

  const first = pages[0];
  const truncated = state.totalPages > count;

  return (
    <div className="flex w-full flex-col gap-2">
      {header}
      <div
        role="region"
        aria-roledescription="carousel"
        aria-label={`PDF document, ${state.totalPages} ${state.totalPages === 1 ? "page" : "pages"}`}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
          // The arrows belong to the document here, not to the page around it.
          e.preventDefault();
          e.stopPropagation();
          go(current + (e.key === "ArrowRight" ? 1 : -1));
        }}
        onTouchStart={(e) => (touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY })}
        onTouchEnd={(e) => {
          const start = touchStart.current;
          touchStart.current = null;
          if (!start) return;
          const dx = e.changedTouches[0].clientX - start.x;
          const dy = e.changedTouches[0].clientY - start.y;
          if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(dy)) go(current + (dx < 0 ? 1 : -1));
        }}
        className="group relative mx-auto w-full overflow-hidden rounded-lg border border-theme-border bg-theme-background outline-none focus-visible:ring-2 focus-visible:ring-[#2f4941]/40"
        style={{ aspectRatio: `${first.width} / ${first.height}`, maxWidth: `min(100%, ${Math.round((520 * first.width) / first.height)}px)` }}
      >
        <div
          data-testid="document-pages-track"
          className="flex h-full transition-transform duration-300 ease-out motion-reduce:transition-none"
          // Sideways swipes turn the page; scrolling and pinch-zoom (small PDF text) stay the phone's.
          style={{ transform: `translateX(-${current * 100}%)`, touchAction: "pan-y pinch-zoom" }}
        >
          {pages.map((page, i) => (
            <div key={page.url} className="h-full w-full flex-none" aria-hidden={i !== current}>
              <img
                src={page.url}
                alt={`Page ${i + 1} of ${state.totalPages}`}
                width={page.width}
                height={page.height}
                loading={i <= 1 ? "eager" : "lazy"}
                draggable={false}
                className="h-full w-full select-none object-contain"
              />
            </div>
          ))}
        </div>

        <span className="pointer-events-none absolute right-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-white">
          {current + 1} / {count}
        </span>

        {current > 0 && (
          <button
            type="button"
            onClick={() => go(current - 1)}
            aria-label="Previous page"
            className={`${ARROW} left-2`}
          >
            <span className="material-icons text-[24px]" aria-hidden="true">
              chevron_left
            </span>
          </button>
        )}
        {current < count - 1 && (
          <button
            type="button"
            onClick={() => go(current + 1)}
            aria-label="Next page"
            className={`${ARROW} right-2`}
          >
            <span className="material-icons text-[24px]" aria-hidden="true">
              chevron_right
            </span>
          </button>
        )}

        {/* Where you are in the document, as LinkedIn's viewer shows it. */}
        {count > 1 && (
          <div className="absolute inset-x-0 bottom-0 h-1 bg-black/10" aria-hidden="true">
            <div
              className="h-full bg-[#2f4941] transition-[width] duration-300 ease-out motion-reduce:transition-none"
              style={{ width: `${((current + 1) / count) * 100}%` }}
            />
          </div>
        )}
      </div>

      {truncated && (
        <p className="px-0.5 text-xs text-theme-fg-muted">
          Showing the first {count} of {state.totalPages} pages. Open the PDF to see them all.
        </p>
      )}
    </div>
  );
};

export default DocumentPagesPreview;
